/* (c) 2020-2023, Nextwave Logic, Inc. All Rights Reserved */

import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import Alert from 'react-s-alert';
import moment from 'moment';
import Calendar from 'react-calendar';
import Joyride, { STATUS } from 'react-joyride';

// context imports
import { ThemeContext } from '../../../contexts/ThemeContext';
import { AuthContext } from '../../../contexts/AuthContext';

// service imports
import DemoService from '../../../services/DemoService';
import GeneralService from '../../../services/GeneralService';
import { counsellors, siteVariable } from '../demoData';

// local imports
import ErrorBoundary from '../../general/ErrorBoundary';
import FullPageLoader from '../../general/FullPageLoader';
import SelectedProvider from './SelectedProvider';
import ClientNavbar from './ClientNavbar';

const BookSession = props => {

    // contexts
    const { theme, fontSize, fontFamily } = useContext(ThemeContext);
    const { selectedTimeZone } = useContext(AuthContext);

    // states
    const [user, setUser] = useState({ firstName: "", lastName: "", email: "", selectedRole: '', roles: [], profileImage: "", demoTakenFor: [] });
    const [isLoading, setIsLoading] = useState(false);
    const [disableSubmitBtn, setDisableSubmitBtn] = useState(false);
    const [selectedDate, setSelectedDate] = useState(moment().add(1, 'day').utcOffset("+05:30").toDate());
    const [includeReport, setIncludeReport] = useState(false);
    const [selectedProvider, setSelectedProvider] = useState({});
    const [loading, setLoading] = useState(true);
    const [listOfAvailableDaysAndBookings, setListOfAvailableDaysAndBookings] = useState([]);
    const [listOfDateAndSlots, setListOfDateAndSlots] = useState([]);
    const [selectedSlotIndex, setSelectedSlotIndex] = useState(-1);
    const [timeSlotSize, setTimeSlotSize] = useState(0);
    const [listOfSlots, setListOfSlots] = useState([]);
    const [selectedTimeSlot, setSelectedTimeSlot] = useState([]);
    const [selectedModeOfService, setSelectedModeOfService] = useState(null);
    const [minDate, setMinDate] = useState(null);
    const [maxDate, setMaxDate] = useState(null);
    const [rescheduleDetails, setRescheduleDetails] = useState({ isRescheduled: false });
    const [clientSubscription, setClientSubscription] = useState({ isActive: false, isCallSessionAvailable: false, isVideoSessionAvailable: false, isInPersonSessionAvailable: false, reportStatus: false });
    const [profileComplete, setProfileComplete] = useState({
        status: true,
        message: {
            msgBody: ['Looks like your profile is incomplete! Please complete that by going to ', <b><Link to='/client/profile'>Profile</Link></b>, ' page to enjoy our service.'],
            msgError: true
        }
    });
    const [isFirstSession, setIsFirstSession] = useState({
        status: false,
        message: {
            msgBody: "Your first session at Umang Wellness is free for 30 minutes, and only phone call is offered during first session",
            msgError: false
        }
    });
    const [tutorial, setTutorial] = useState({
        status: false,
        steps: [
            {
                content: <h2>You can book a new session from here.</h2>,
                placement: 'center',
                target: '#bookSessionDiv',
            },
            {
                content: <div className="tooltipDiv">Select a date for the session</div>,
                placement: 'right',
                target: '#selectDate'
            },
            {
                content: <div className="tooltipDiv">Select the time slot(s)<br />You can select a maximum 2 adjacent slots.</div>,
                placement: 'right',
                target: '#slotList'
            },
            {
                content: <div className="tooltipDiv">Select the mode of service you prefer.<br />The prices may vary depending on your choice of counsellor.</div>,
                placement: 'top',
                target: '#selectMode'
            },
            {
                content: <div className="tooltipDiv">Select if you wish to have a session report from your counsellor at the end of all the sessions.<br />There will be a one-time charge for the session report.</div>,
                placement: 'top',
                target: '#sessionReport'
            },
            {
                content: <div className="tooltipDiv">Click here to proceed for payment</div>,
                placement: 'top',
                target: '#saveBtn'
            }
        ]
    });
    let helpers = null;

    const getSlots = async (availableTimes, timeZone, j, size) => {
        let listOfSlots = [];
        await Promise.all(
            availableTimes.map(async (item, index) => {
                let tempFrom = moment().utcOffset(timeZone).add(j + 1, 'day').set({ hour: moment(item.from).utcOffset(timeZone).get('hour'), minute: moment(item.from).utcOffset(timeZone).get('minute'), second: 0, millisecond: 0 });
                let tempTo = moment().utcOffset(timeZone).add(j + 1, 'day').set({ hour: moment(item.to).utcOffset(timeZone).get('hour'), minute: moment(item.to).utcOffset(timeZone).get('minute'), second: 0, millisecond: 0 });
                let slots = listOfSlots;
                while (!tempFrom.isSame(tempTo)) {
                    let slot = { from: "", to: "", availableStatus: Math.random() * 10 > 8 ? false : true };
                    slot.from = tempFrom.toISOString();
                    tempFrom = moment(tempFrom).set({ second: 0, millisecond: 0 }).add(size, 'minutes');
                    slot.to = tempFrom.toISOString();
                    slots.push(slot);
                }
                listOfSlots = slots;
            })
        )
        return listOfSlots;
    }

    // useEffects
    useEffect(() => {
        DemoService.getUserData().then(data => {
            if (data.isAuthenticated) {
                if (data.user.roles[0] === "DemoClient") {
                    setUser(data.user);
                    if (!data.user.demoTakenFor.includes("BookSession")) {
                        setTutorial({ ...tutorial, status: true });
                    }
                    let selectedProvider = { ...counsellors[0], isTrue: true, name: `${counsellors[0].title ? counsellors[0].title : ''} ${counsellors[0].firstName} ${counsellors[0].lastName}`, GSTCharge: siteVariable.GSTPercentage };
                    setProfileComplete({ ...profileComplete, status: selectedProvider.profileComplete });
                    setSelectedProvider(selectedProvider);
                    let isCallSessionAvailable = true;
                    let isVideoSessionAvailable = true;
                    let isInPersonSessionAvailable = false;
                    let reportStatus = true;
                    setClientSubscription({ isActive: true, isCallSessionAvailable, isVideoSessionAvailable, isInPersonSessionAvailable, reportStatus });
                    try {
                        Promise.all([...Array(siteVariable.advanceBookingLimit)].map(async (item, j) => {
                            let slotsToAdd = await getSlots(selectedProvider.availableTimes, selectedProvider.timeZone, j, siteVariable.timeSlotSize);
                            selectedProvider.availableDaysAndBookedTimings = selectedProvider.availableDaysAndBookedTimings.concat(slotsToAdd);
                        })).then(() => {
                            setMinMaxDates(selectedProvider.availableDaysAndBookedTimings);
                            setTimeSlotSize(siteVariable.timeSlotSize);
                            setListOfAvailableDaysAndBookings(selectedProvider.availableDaysAndBookedTimings);
                            // setSlots(data.timeSlotSize, selectedProvider.availableTimes);
                            // updateDateIndex();
                            if (!rescheduleDetails.isRescheduled && user.bookSessionDetails) {
                                setSelectedDate(user.bookSessionDetails.selectedDate);
                                setSelectedTimeSlot(user.bookSessionDetails.selectedTimeSlot);
                                setSelectedModeOfService(user.bookSessionDetails.selectedModeOfService);
                            }
                        })
                    } catch (err) {
                        Alert.error("Some error occured, please try again later or report support team.", {
                            position: 'top-right',
                            effect: 'slide'
                        });
                        GeneralService.logError('at Client BookSession useEffect2_2', 'Error in try catch block', err.stack);
                    }
                    setLoading(false);
                } else {
                    props.history.replace('PagePath');
                }
            } else {
                props.history.replace('/demo/signin');
            }
        })
    }, [])

    useEffect(() => {
        try {
            if (props.location.state) {
                setRescheduleDetails(props.location.state);
                setSelectedDate(moment(props.location.state.selectedDate).utcOffset(selectedTimeZone.isDST ? selectedTimeZone.dst_offset : selectedTimeZone.offset).toDate());
                setSelectedModeOfService(props.location.state.modeOfServiceSelected);
                setLoading(false);
            }
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at Client BookSession useEffect1', 'Error in try catch block', err.stack);
        }
    }, [props.location.state]);

    useEffect(() => {
        if (minDate && maxDate) {
            setMinMaxDates();
        }
    }, [selectedTimeZone.offset]);

    useEffect(() => {
        if (listOfDateAndSlots.length > 0)
            updateDateIndex(selectedDate);
    }, [listOfDateAndSlots]);

    const setMinMaxDates = (list) => {
        let tempListOfAvailableDaysAndBookings = [];
        if (list) {
            tempListOfAvailableDaysAndBookings = list;
        } else {
            tempListOfAvailableDaysAndBookings = listOfAvailableDaysAndBookings;
        }
        createSlots(tempListOfAvailableDaysAndBookings);
        let startDate = moment(tempListOfAvailableDaysAndBookings[0].from).utcOffset(selectedTimeZone.isDST ? selectedTimeZone.dst_offset : selectedTimeZone.offset);
        let endDate = moment(tempListOfAvailableDaysAndBookings[tempListOfAvailableDaysAndBookings.length - 1].to).utcOffset(selectedTimeZone.isDST ? selectedTimeZone.dst_offset : selectedTimeZone.offset);
        // console.log("startDate", startDate.format("DD-MM-YYYY HH:mm"));
        // console.log("endDate", endDate.format("DD-MM-YYYY HH:mm"));
        startDate = new Date(startDate.get('year'), startDate.get('month'), startDate.get('date'));
        endDate = new Date(endDate.get('year'), endDate.get('month'), endDate.get('date'));
        setMinDate(startDate);
        setMaxDate(endDate);
        setSelectedDate(startDate);
    }

    const updateDateIndex = (e) => {
        const date = moment(e).format("DD-MM-YYYY");
        const dateIndex = listOfDateAndSlots.findIndex(item => date === moment(item[1].from).utcOffset(selectedTimeZone.isDST ? selectedTimeZone.dst_offset : selectedTimeZone.offset).format("DD-MM-YYYY"));
        setSelectedSlotIndex(dateIndex);
    }

    // handle functions
    const handleDatePickerChange = e => {
        try {
            setSelectedDate(e);
            updateDateIndex(e);
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at Client BookSession handleDatePickerChange', 'Error in try catch block', err.stack);
        }
    }

    const handleReschedule = e => {
        try {
            if (selectedDate == null) {
                Alert.error("Please select date", {
                    position: 'top-right',
                    effect: 'slide'
                });
            }
            if (selectedTimeSlot.length !== rescheduleDetails.selectedTime.length) {
                Alert.error(`Please select ${rescheduleDetails.selectedTime.length} timeslot`, {
                    position: 'top-right',
                    effect: 'slide'
                });
            }
            if (selectedModeOfService == null) {
                Alert.error("Please select mode of service", {
                    position: 'top-right',
                    effect: 'slide'
                });
            }
            if (selectedDate != null && selectedTimeSlot.length === rescheduleDetails.selectedTime.length && selectedModeOfService != null) {
                setIsLoading(true);
                setTimeout(() => {
                    Alert.info("Session successfully rescheduled", {
                        position: 'top-right',
                        effect: 'slide'
                    });
                    setIsLoading(false);
                    props.history.push('dashboard');
                }, [2000])
            }
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            setIsLoading(false);
            GeneralService.logError('at Client BookSession handleReschedule', 'Error in try catch block', err.stack);
        }
    }

    const handleSubmit = e => {
        try {
            if (!profileComplete.status) {
                Alert.error("Please complete your profile first", {
                    position: 'top-right',
                    effect: 'slide'
                });
            } else {
                if (selectedDate == null) {
                    Alert.error("Please select date", {
                        position: 'top-right',
                        effect: 'slide'
                    });
                }
                if (selectedTimeSlot.length < 1) {
                    Alert.error("Please select timeslot", {
                        position: 'top-right',
                        effect: 'slide'
                    });
                }
                if (selectedModeOfService == null) {
                    Alert.error("Please select mode of service", {
                        position: 'top-right',
                        effect: 'slide'
                    });
                }
                if (selectedDate != null && selectedTimeSlot.length != 0 && selectedModeOfService != null) {
                    setDisableSubmitBtn(true);
                    const timeSlots = selectedTimeSlot.length > 1 ? [listOfDateAndSlots[selectedSlotIndex][selectedTimeSlot[0]], listOfDateAndSlots[selectedSlotIndex][selectedTimeSlot[1]]] : [listOfDateAndSlots[selectedSlotIndex][selectedTimeSlot[0]]];
                    setUser({
                        ...user, bookSessionDetails: {
                            selectedDate,
                            selectedTimeSlot,
                            selectedModeOfService
                        }
                    });
                    let tempDate = moment(selectedDate).utcOffset(selectedTimeZone.isDST ? selectedTimeZone.dst_offset : selectedTimeZone.offset).set({
                        hour: moment(timeSlots[0].from).utcOffset(selectedTimeZone.isDST ? selectedTimeZone.dst_offset : selectedTimeZone.offset).get('hour'),
                        minute: moment(timeSlots[0].from).utcOffset(selectedTimeZone.isDST ? selectedTimeZone.dst_offset : selectedTimeZone.offset).get('minute')
                    }).toISOString();

                    let tempTimeSlot = timeSlots.map(item => {
                        return { ...item, isLocked: true, lockedAt: moment().utcOffset(0).toISOString(), lockedBy: user.email }
                    });

                    setDisableSubmitBtn(false);
                    props.history.push({
                        pathname: "/demo/client/makepayment",
                        state: {
                            selectedProvider: selectedProvider,
                            selectedDate: tempDate,
                            selectedTimeSlot: tempTimeSlot,
                            modeOfServiceSelected: selectedModeOfService,
                            isFirstSession: isFirstSession.status,
                            isReportIncluded: {
                                status: includeReport,
                                amount: includeReport ? selectedProvider.reportCharge : 0
                            }
                        }
                    });
                }
            }
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at Client BookSession handleSubmit', 'Error in try catch block', err.stack);
        }
    }

    // utility functions
    const toggleIncludeReport = e => {
        try {
            setIncludeReport(!includeReport);
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at Client BookSession toggleIncludeReport', 'Error in try catch block', err.stack);
        }
    }

    const selectTimeSlot = (index) => {
        try {
            if (selectedTimeSlot.includes(index)) {
                setSelectedTimeSlot(selectedTimeSlot.filter(item => item !== index));
            } else if (selectedTimeSlot.length < 2) {
                if (selectedTimeSlot.length == 0) {
                    setSelectedTimeSlot([index]);
                } else {
                    if (isFirstSession.status && selectedTimeSlot.length === 1) {
                        setSelectedTimeSlot([index]);
                        // Alert.error(`Please select only 1 timeslot`, {
                        //     position: 'top-right',
                        //     effect: 'slide'
                        // });
                        return;
                    }
                    if (rescheduleDetails.isRescheduled) {
                        if (selectedTimeSlot.length == rescheduleDetails.selectedTime.length) {
                            Alert.error(`Please select only ${rescheduleDetails.selectedTime.length} timeslot`, {
                                position: 'top-right',
                                effect: 'slide'
                            });
                            return;
                        }
                    }
                    if (Math.abs(moment(listOfDateAndSlots[selectedSlotIndex][index].from).diff(moment(listOfDateAndSlots[selectedSlotIndex][selectedTimeSlot[0]].from), 'minutes')) === timeSlotSize) {
                        if (selectedTimeSlot[0] < index) {
                            setSelectedTimeSlot([...selectedTimeSlot, index]);
                        } else {
                            setSelectedTimeSlot([index, ...selectedTimeSlot]);
                        }
                    } else {
                        Alert.error("Must select consecutive slots", {
                            position: 'top-right',
                            effect: 'slide'
                        });
                    }
                }
            } else {
                Alert.error("Cannot select more than 2 slots", {
                    position: 'top-right',
                    effect: 'slide'
                });
            }
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at Client BookSession selectTimeSlot', 'Error in try catch block', err.stack);
        }
    }

    const selectModeOfService = (mode) => {
        try {
            setSelectedModeOfService(mode);
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at Client BookSession selectModeOfService', 'Error in try catch block', err.stack);
        }
    }

    const createSlots = (list) => {
        let slots = [];
        let dateSlots = [];
        let rangeIndex = 1;
        dateSlots.push({ isExtra: true, value: rangeIndex });
        let date = "";
        Promise.all(
            list.map((item, index) => {
                // if (item.availableStatus && item.from) {
                if (date === "") {
                    date = moment(item.from).utcOffset(selectedTimeZone.isDST ? selectedTimeZone.dst_offset : selectedTimeZone.offset).format("DD-MM-YYYY");
                } else if (date !== moment(item.from).utcOffset(selectedTimeZone.isDST ? selectedTimeZone.dst_offset : selectedTimeZone.offset).format("DD-MM-YYYY")) {
                    slots.push(dateSlots);
                    date = "";
                    dateSlots = [];
                    rangeIndex = 0;
                } else if (index === (list.length - 1)) {
                    slots.push(dateSlots);
                }
                if (index > 0 && moment(list[index - 1].to).diff(moment(item.from), 'minute') !== 0) {
                    rangeIndex = rangeIndex + 1;
                    dateSlots.push({ isExtra: true, value: rangeIndex });
                }
                dateSlots.push(item);
                // }
            })
        ).then(() => {
            setListOfDateAndSlots(slots);
        })
    }

    const handleJoyrideCallback = (data) => {
        const { status, type } = data;
        const finishedStatuses = [STATUS.FINISHED, STATUS.SKIPPED];

        if (finishedStatuses.includes(status)) {
            if (!user.demoTakenFor.includes("BookSession")) {
                DemoService.markVisited("BookSession");
                setUser({ ...user, demoTakenFor: [...user.demoTakenFor, "BookSession"] });
            }
            setTutorial({ ...tutorial, status: false });
        }
    };

    const getHelpers = (paramHelpers) => {
        helpers = paramHelpers;
    };

    // function's return
    return (
        loading ?
            <FullPageLoader /> :
            <div id="bookSessionDiv">
                <ErrorBoundary errorAt="At Client BookSession Joyride">
                    <Joyride
                        callback={handleJoyrideCallback}
                        continuous={true}
                        getHelpers={getHelpers}
                        run={tutorial.status}
                        scrollToFirstStep={true}
                        showProgress={true}
                        showSkipButton={true}
                        steps={tutorial.steps}
                        locale={{ last: 'Finish' }}
                        styles={{
                            options: {
                                primaryColor: "rgb(56, 78, 115)",
                                zIndex: 10000,
                            },
                        }}
                    />
                </ErrorBoundary>
                <ErrorBoundary errorAt="At Client BookSession ClientNavbar">
                    <ClientNavbar history={props.history} showTutorial={() => setTutorial({ ...tutorial, status: true })} />
                </ErrorBoundary>
                <div className="p-3">

                    <div>
                        <div className="d-flex">
                            <h1 id="booksession-heading" style={theme.headingBold}>Book Session</h1>
                        </div>
                        <hr style={{ borderWidth: "2px" }} />
                    </div>

                    <div className="p-3">
                        <h2 id="mycounsellorheading" style={theme.subHeadingSemibold}>My Counsellor</h2>
                        <hr />

                        <ErrorBoundary errorAt="At Client BookSession SelectedProvider">
                            <SelectedProvider
                                history={props.history}
                                selectedProvider={selectedProvider}
                                selectedTimeZone={selectedTimeZone}
                            />
                        </ErrorBoundary>
                    </div>

                    <div className="p-3">
                        <h2 id="select-date-heading" style={theme.subHeadingSemibold}>Select Date</h2>
                        <hr />

                        {
                            listOfAvailableDaysAndBookings.length ?
                                <div>
                                    <div className="row px-3">
                                        <div id="selectDate">
                                            <ErrorBoundary errorAt="At Client BookSession Calendar">
                                                <Calendar
                                                    className="m-2 rounded"
                                                    onChange={handleDatePickerChange}
                                                    value={selectedDate}
                                                    minDate={minDate}
                                                    maxDate={maxDate}
                                                />
                                            </ErrorBoundary>
                                        </div>
                                    </div>
                                    <br />

                                    <div className="mt-4">
                                        <h3 style={theme.h3headingSemibold}>{isFirstSession.status ? "Select Time Slot" : "Select Time Slot(s) -- Maximum 2 from any one Time Range"}</h3>
                                        <ErrorBoundary errorAt="At Client BookSession SelectTimeSlots">
                                            <div id="slotList" className="row px-3 justify-content-md-start justify-content-sm-between justify-content-between">
                                                {
                                                    selectedSlotIndex > -1 && listOfDateAndSlots[selectedSlotIndex].length > 0
                                                        ?
                                                        listOfDateAndSlots[selectedSlotIndex].map((item, index) => (
                                                            item.isExtra ?
                                                                <div key={index} id={`slot-time-range${index}`} className="col-12 mt-3" style={theme.extraSlotCard}>
                                                                    <span><span style={theme.paragraphMedium}>Time Range -</span> {item.value}</span>
                                                                    <hr className="p-0 mt-0 mb-1" />
                                                                </div>
                                                                :
                                                                item.availableStatus && !item.isLocked && !item.isBooked ?
                                                                    <div key={index} id={`slot${index}`} className="col-sm-5 col-5 col-md-2 m-2 text-center py-5" style={selectedTimeSlot.includes(index) ? theme.selectedSlotCard : theme.slotCard} onClick={() => selectTimeSlot(index)}>
                                                                        <span>{`${moment(item.from).utcOffset(selectedTimeZone.isDST ? selectedTimeZone.dst_offset : selectedTimeZone.offset).set({ second: 0, millisecond: 0 }).format("hh:mm A")} - ${moment(item.to).utcOffset(selectedTimeZone.isDST ? selectedTimeZone.dst_offset : selectedTimeZone.offset).set({ second: 0, millisecond: 0 }).format("hh:mm A")}`}</span>
                                                                    </div>
                                                                    :
                                                                    <div key={index} id={`slot${index}`} className="col-sm-5 col-5 col-md-2 m-2 text-center py-5" style={theme.disabledSlotCard}>
                                                                        <span>{`${moment(item.from).utcOffset(selectedTimeZone.isDST ? selectedTimeZone.dst_offset : selectedTimeZone.offset).set({ second: 0, millisecond: 0 }).format("hh:mm A")} - ${moment(item.to).utcOffset(selectedTimeZone.isDST ? selectedTimeZone.dst_offset : selectedTimeZone.offset).set({ second: 0, millisecond: 0 }).format("hh:mm A")}`}</span>
                                                                    </div>
                                                        ))
                                                        :
                                                        <h3>No slots available on this day</h3>
                                                }
                                                <div className="col-12 py-2 mt-2 mb-2" style={theme.extraSlotCard}>
                                                    {/* <span>{item.value}</span> */}
                                                </div>
                                            </div>
                                        </ErrorBoundary>
                                    </div>

                                    <ErrorBoundary errorAt="At Client BookSession SelectMode">
                                        <div className="mt-4">
                                            <h3 id="mode-of-service-heading" style={theme.h3headingSemibold}>Select Mode of Service</h3>
                                            {
                                                rescheduleDetails.isRescheduled
                                                    ?
                                                    <span>{rescheduleDetails.modeOfServiceSelected}</span>
                                                    :
                                                    <div id="selectMode" className="row px-3">
                                                        {
                                                            selectedProvider.modeOfService.call.rate === 0 ? null :
                                                                <div id="callService" className="col-sm-5 col-5 col-md-3 m-2 text-center py-2" style={selectedModeOfService == selectedProvider.modeOfService.call ? theme.selectedSlotCard : theme.slotCard} onClick={() => selectModeOfService(selectedProvider.modeOfService.call)}>
                                                                    <label style={theme.subHeadingSemibold}>{selectedProvider.modeOfService.call.name}</label>
                                                                    <hr className="mt-0" />
                                                                    {
                                                                        clientSubscription.isCallSessionAvailable ?
                                                                            <label>Included under subscription</label>
                                                                            :
                                                                            isFirstSession.status
                                                                                ? <label>Rate - ₹0 / {selectedTimeSlot.length === 2 ? timeSlotSize * 2 : timeSlotSize} minutes</label>
                                                                                : <label>Rate - ₹{selectedTimeSlot.length === 2 ? selectedProvider.modeOfService.call.rate * 2 : selectedProvider.modeOfService.call.rate} / {selectedTimeSlot.length === 2 ? timeSlotSize * 2 : timeSlotSize} minutes</label>
                                                                    }
                                                                </div>
                                                        }
                                                        {
                                                            isFirstSession.status ?
                                                                null :
                                                                [
                                                                    selectedProvider.modeOfService.chat.rate === 0 ? null :
                                                                        <div key="chat" className="col-sm-5 col-5 col-md-3 m-2 text-center py-2" style={selectedModeOfService == selectedProvider.modeOfService.chat ? theme.selectedSlotCard : theme.slotCard} onClick={() => selectModeOfService(selectedProvider.modeOfService.chat)}>
                                                                            <label style={theme.subHeadingSemibold}>{selectedProvider.modeOfService.chat.name}</label>
                                                                            <hr className="mt-0" />
                                                                            {
                                                                                clientSubscription.isActive ?
                                                                                    <label>Included under subscription</label>
                                                                                    :
                                                                                    isFirstSession.status
                                                                                        ? <label>Rate - ₹0 / {selectedTimeSlot.length === 2 ? timeSlotSize * 2 : timeSlotSize} minutes</label>
                                                                                        : <label>Rate - ₹{selectedTimeSlot.length === 2 ? selectedProvider.modeOfService.chat.rate * 2 : selectedProvider.modeOfService.chat.rate} / {selectedTimeSlot.length === 2 ? timeSlotSize * 2 : timeSlotSize} minutes</label>
                                                                            }
                                                                        </div>,
                                                                    selectedProvider.modeOfService.video.rate === 0 ? null :
                                                                        <div key="video" className="col-sm-5 col-5 col-md-3 m-2 text-center py-2" style={selectedModeOfService == selectedProvider.modeOfService.video ? theme.selectedSlotCard : theme.slotCard} onClick={() => selectModeOfService(selectedProvider.modeOfService.video)}>
                                                                            <label style={theme.subHeadingSemibold}>{selectedProvider.modeOfService.video.name}</label>
                                                                            <hr className="mt-0" />
                                                                            {
                                                                                clientSubscription.isVideoSessionAvailable ?
                                                                                    <label>Included under subscription</label>
                                                                                    :
                                                                                    isFirstSession.status
                                                                                        ? <label>Rate - ₹0 / {selectedTimeSlot.length === 2 ? timeSlotSize * 2 : timeSlotSize} minutes</label>
                                                                                        : <label>Rate - ₹{selectedTimeSlot.length === 2 ? selectedProvider.modeOfService.video.rate * 2 : selectedProvider.modeOfService.video.rate} / {selectedTimeSlot.length === 2 ? timeSlotSize * 2 : timeSlotSize} minutes</label>
                                                                            }
                                                                        </div>,
                                                                    selectedProvider.modeOfService.inPerson.rate === 0 ? null :
                                                                        <div key="inPerson" className="col-sm-5 col-5 col-md-3 m-2 text-center py-2" style={selectedModeOfService == selectedProvider.modeOfService.inPerson ? theme.selectedSlotCard : theme.slotCard} onClick={() => selectModeOfService(selectedProvider.modeOfService.inPerson)}>
                                                                            <label style={theme.subHeadingSemibold}>{selectedProvider.modeOfService.inPerson.name}</label>
                                                                            <hr className="mt-0" />
                                                                            {
                                                                                clientSubscription.isInPersonSessionAvailable ?
                                                                                    <label>Included under subscription</label>
                                                                                    :
                                                                                    isFirstSession.status
                                                                                        ? <label>Rate - ₹0 / {selectedTimeSlot.length === 2 ? timeSlotSize * 2 : timeSlotSize} minutes</label>
                                                                                        : <label>Rate - ₹{selectedTimeSlot.length === 2 ? selectedProvider.modeOfService.inPerson.rate * 2 : selectedProvider.modeOfService.inPerson.rate} / {selectedTimeSlot.length === 2 ? timeSlotSize * 2 : timeSlotSize} minutes</label>
                                                                            }
                                                                        </div>
                                                                ]
                                                        }
                                                    </div>
                                            }
                                        </div>
                                    </ErrorBoundary>

                                    {
                                        isFirstSession.status || selectedProvider.modeOfService.chat.rate === 0 ? null :
                                            <div className="mt-4">
                                                <ErrorBoundary errorAt="At Client BookSession IncludeReport">
                                                    <h3 id="include-report" style={theme.h3headingSemibold}>Include report</h3>
                                                    {
                                                        rescheduleDetails.isRescheduled || selectedProvider.isReportIncluded
                                                            ?
                                                            selectedProvider.isReportIncluded ? <span>Report is included</span> : <span>Report is not included</span>
                                                            :
                                                            <div className="row px-3">
                                                                <div id="sessionReport" className="col-sm-5 col-5 col-md-3 m-2 text-center py-2" style={includeReport ? theme.selectedSlotCard : theme.slotCard} onClick={toggleIncludeReport}>
                                                                    <label style={theme.subHeadingSemibold}>Session report</label>
                                                                    <hr className="mt-0" />
                                                                    {
                                                                        clientSubscription.reportStatus ?
                                                                            <label>Included under subscription</label>
                                                                            :
                                                                            <label> Rate - ₹{selectedProvider.reportCharge}</label>
                                                                    }
                                                                </div>
                                                            </div>
                                                    }
                                                </ErrorBoundary>
                                            </div>
                                    }
                                </div>
                                :
                                <h3>No dates available for selected counsellor</h3>
                        }
                    </div>

                    {
                        listOfAvailableDaysAndBookings.length ?
                            rescheduleDetails.isRescheduled
                                ?
                                <button type="button" className="btn mt-5" style={theme.button} onClick={handleReschedule} disabled={isLoading}>{isLoading ? <span className="spinner-border spinner-border-sm mr-1"></span> : null}Reschedule</button>
                                :
                                <div className="mt-5">
                                    <ErrorBoundary errorAt="At Client BookSession GSTCharge">
                                        <p style={{ fontFamily: fontFamily.primary, fontSize: fontSize.sm }}>*{selectedProvider.GSTCharge}% GST will be applicable on total amount</p>
                                        <button className="btn" style={theme.button} disabled={disableSubmitBtn} onClick={handleSubmit} id="saveBtn">{disableSubmitBtn ? <span className="spinner-border spinner-border-sm mr-1"></span> : null}Proceed to Payment</button>
                                    </ErrorBoundary>
                                </div>
                            : null
                    }


                </div >
            </div >
    )

}

export default BookSession;